import React, { useCallback, useId, useState } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import TitleIcon from '@mui/icons-material/Title';
import type { NoteVariant } from '../types';

export interface NoteVariantSelectButtonProps {
	value: NoteVariant;
	onChange: (value: NoteVariant) => void;
}

const OPTIONS: { value: NoteVariant; label: string; Icon: typeof StickyNote2Icon }[] = [
	{ value: 'default', label: 'Short', Icon: StickyNote2Icon },
	{ value: 'long', label: 'Long', Icon: ArticleOutlinedIcon },
	{ value: 'header', label: 'Header', Icon: TitleIcon },
];

/** Same interaction as {@link CanvasAddElementSelectButton}: chevron opens a menu. */
export default function NoteVariantSelectButton({ value, onChange }: NoteVariantSelectButtonProps) {
	const uid = useId();
	const buttonId = `${uid}-note-variant-button`;
	const menuId = `${uid}-note-variant-menu`;
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const menuOpen = Boolean(anchorEl);

	const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handlePick = useCallback(
		(next: NoteVariant) => {
			onChange(next);
			setAnchorEl(null);
		},
		[onChange],
	);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const selectedOption = OPTIONS.find((option) => option.value === value)?? OPTIONS[0];

	return (
		<div>
			<IconButton
				size="small"
				id={buttonId}
				aria-controls={menuOpen ? menuId : undefined}
				aria-haspopup="true"
				aria-expanded={menuOpen ? 'true' : undefined}
				aria-label="Canvas appearance"
				onClick={handleOpen}
				tabIndex={-1}
				title={selectedOption.label}
			>
				<selectedOption.Icon fontSize="small" sx={{ color: 'primary.main' }} />
			</IconButton>
			<Menu
				id={menuId}
				anchorEl={anchorEl}
				open={menuOpen}
				onClose={handleClose}
				MenuListProps={{
					'aria-labelledby': buttonId,
				}}
			>
				{OPTIONS.map(({ value: optionValue, label, Icon }) => (
					<MenuItem
						key={optionValue}
						selected={optionValue === value}
						onClick={() => handlePick(optionValue)}
						sx={{ gap: 1 }}
					>
						<Icon fontSize="small" sx={{ color: 'primary.main' }} />
						{label}
					</MenuItem>
				))}
			</Menu>
		</div>
	);
}
